import { useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons';
import { classNames } from '@progress/kendo-react-common';
import { 
    DateTabs,
    HasAdjustmnentsFilter,
    Icon,
    InvoiceCard, 
    InvoiceSortFilter,
    InvoiceStatusFilter,
    PaginatedList,
    SearchInput,
    SuspenseBoundary,
    UploadInvoiceButton,
} from 'components';
import { useAccountParam, useVisibility } from 'hooks';
import { 
    useFilteredInvoices, 
    useInvoiceFilter, 
    useRefreshFilteredInvoices, 
} from 'state';
import styles from './InvoicesPage.module.scss';

export default function InvoicesPage() {
    const accountId = useAccountParam();
    const {
        dateRange,
        setDateRange,
        status,
        hasAdjustments,
        searchTerm,
        sort,
        setStatus,
        setHasAdjustments,
        setSearchTerm,
        setSort,
    } = useInvoiceFilter();
    const refresh = useRefreshFilteredInvoices(accountId);

    const headerRef = useRef<HTMLDivElement | null>(null);
    const shouldShowCombinedHeader = useVisibility(headerRef);

    const Headers = useMemo(() => (
        <div className={styles.pageHeader}>
            {shouldShowCombinedHeader ? 
                <div className={styles.title}>               
                    <span>
                        <Icon icon={faFileInvoice}/> 
                    </span>
                    <span>
                        Invoices 
                    </span>
                </div>
                : <></>}
            <DateTabs
                dateRange={dateRange}
                setDateRange={setDateRange}
            />
        </div>
    ),[dateRange, setDateRange, shouldShowCombinedHeader]);

    const Filters = useMemo(() => (
        <div className={styles.filters}>
            <InvoiceStatusFilter
                onStatusChange={setStatus}
                status={status}
            />
            <HasAdjustmnentsFilter
                onStatusChange={setHasAdjustments}
                hasAdjustments={hasAdjustments}
            />
            <SearchInput
                value={searchTerm ?? ''}
                onValueChange={setSearchTerm}
            />
            <InvoiceSortFilter
                onInvoiceSortChange={setSort}
                invoiceSort={sort}
            />
        </div>
    ),[hasAdjustments, searchTerm, setHasAdjustments, setSearchTerm, setSort, setStatus, sort, status])

    const FilterAndHeader = useMemo(() => (
        <>
            {Filters}
            {Headers}
        </>
    ), [Filters, Headers]);

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={classNames({[styles.hidden] : !shouldShowCombinedHeader})} ref={headerRef}>
                    {Headers}
                </div>
                <div className={classNames(styles.fixedHeaderAndFilter, {[styles.hidden] : shouldShowCombinedHeader })}>
                    {FilterAndHeader}
                </div>
                <div className={styles.content}>
                    <div className={styles.uploadInvoiceButton}>
                        <UploadInvoiceButton />
                    </div>
                    <SuspenseBoundary errorMessage={`Error retrieving invoices for account ${accountId}`} onReset={()=>{}}>
                        <div className={classNames(styles.filters, {[styles.hidden] : !shouldShowCombinedHeader})}>
                            {Filters}
                        </div>
                    </SuspenseBoundary>
                    {accountId ? (
                        <SuspenseBoundary
                            errorMessage={`Error retrieving invoices for account ${accountId}`}
                            onReset={refresh}
                        >
                            <InvoicesComponent />
                        </SuspenseBoundary>
                        ) : <div className={styles.message}>Please select an account to view invoices.</div>
                    }
                </div>
            </div>
        </div>
    );
};

const InvoicesComponent = () => {
    const accountId = useAccountParam();
    const invoices = useFilteredInvoices(accountId);
    return !_.isEmpty(invoices) ? (
        <>
            <PaginatedList
                items={invoices}
                componentKey='Invoices'
                resetKey={accountId}
            >
                {
                    (item) => (
                        <InvoiceCard
                            key={item.invoiceId + '_' + item.rowNumber}
                            invoice={item}
                        />  
                    )
                }
            </PaginatedList>           
        </>
    ) : <div className={styles.message}>No invoices found for the selected date range and filters.</div>;
};